import React, { useEffect, useRef } from 'react';
import { useMap, Rectangle } from 'react-leaflet';
import * as turf from '@turf/turf';

const isValidBounds = (bounds) => {
  return Array.isArray(bounds) && 
         bounds.length === 2 && 
         Array.isArray(bounds[0]) && 
         Array.isArray(bounds[1]) && 
         bounds[0].length === 2 && 
         bounds[1].length === 2 && 
         !bounds.flat().some(isNaN);
};

const EditableRectangle = ({ bounds, setBounds, km2, aspectRatio, onCenterChange }) => {
  const map = useMap();
  const lastSizeRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    const computeBoundsFor = (center) => {
      const areaInMeters = km2 * 1000000;
      const height = Math.sqrt(areaInMeters / aspectRatio);
      const width = height * aspectRatio;
      const southPoint = turf.destination([center.lng, center.lat], height / 2, 180, { units: 'meters' });
      const northPoint = turf.destination([center.lng, center.lat], height / 2, 0, { units: 'meters' });
      const eastPoint = turf.destination([center.lng, center.lat], width / 2, 90, { units: 'meters' });
      const westPoint = turf.destination([center.lng, center.lat], width / 2, -90, { units: 'meters' });
      return [
        [southPoint.geometry.coordinates[1], westPoint.geometry.coordinates[0]],
        [northPoint.geometry.coordinates[1], eastPoint.geometry.coordinates[0]],
      ];
    };

    // Programmatic moves at mount (ViewRestorer's setView, place-search
    // fitBounds, container size hydration) fire moveend within the
    // first few hundred ms. Without an armed guard, those would
    // overwrite restored bounds OR (when bounds is null) auto-create
    // a rectangle before the user has touched anything. The flag flips
    // after a short delay so genuine user pans still come through.
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 600);

    const updateRectangle = () => {
      // Detect a moveend that was triggered by a container size change
      // (e.g. side panel toggling) and ignore it. Without this, the
      // rectangle silently re-centers around the new viewport center every
      // time the map is resized.
      const currentSize = map.getSize();
      if (
        lastSizeRef.current &&
        (lastSizeRef.current.x !== currentSize.x || lastSizeRef.current.y !== currentSize.y)
      ) {
        lastSizeRef.current = currentSize;
        return;
      }
      lastSizeRef.current = currentSize;

      if (!armed) return;

      const center = map.getCenter();
      const newBounds = computeBoundsFor(center);
      if (isValidBounds(newBounds)) {
        setBounds(newBounds);
        onCenterChange([center.lat, center.lng]);
      }
    };

    lastSizeRef.current = map.getSize();

    map.on('moveend', updateRectangle);
    map.on('zoomend', updateRectangle);

    return () => {
      clearTimeout(armTimer);
      map.off('moveend', updateRectangle);
      map.off('zoomend', updateRectangle);
    };
  }, [map, setBounds, km2, aspectRatio]);

  if (!isValidBounds(bounds)) {
    return null;
  }

  return (
    <Rectangle 
      bounds={bounds} 
      pathOptions={{ color: 'blue', weight: 2 }}
    />
  );
};

export default EditableRectangle;